import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  IconButton,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  Chip,
} from '@mui/material';
// Import DatePicker and LocalizationProvider from the correct packages
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Share as ShareIcon,
  AttachFile as AttachFileIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Comment as CommentIcon,
  Send as SendIcon,
  ThumbUp as ThumbUpIcon,
  InsertEmoticon as EmojiIcon,
  ArrowForwardIos,
  ArrowBack,
  Info as InfoIcon,
  Alarm as AlarmIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material'; // Import from correct location
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { ClickAwayListener } from '@mui/base';
import { alpha } from '@mui/material/styles';
import MentionInput from '../components/MentionInput';
import { processMentionsAndNotify, extractMentions, getUserIdsFromMentions, createReminderNotificationsForMentions, activateGlobalScheduledReminders } from '../services/mention.service';
import { sendSMS } from '../services/semaphore';
import { TextFieldProps } from '@mui/material/TextField';
import { useNotifications } from '../contexts/NotificationsContext';
import { cleanLoandiskUniqueNumber } from '../utils/loandiskId';

// Profile header styles - updated for responsiveness
const ProfileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(4),
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column-reverse',
    alignItems: 'center',
    textAlign: 'center',
    gap: theme.spacing(2),
  }
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  backgroundColor: theme.palette.grey[200],
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  border: '4px solid white',
  [theme.breakpoints.down('sm')]: {
    width: 100,
    height: 100,
  }
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  color: theme.palette.text.secondary,
}));

const UpdateInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    '& fieldset': { borderColor: theme.palette.divider },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
  },
}));

// Buckets for storage uploads
const STORAGE_BUCKET = 'updates';
const BORROWER_PHOTO_BUCKET = 'borrower_photos';

// Interfaces
interface UpdateComment {
  id: string;
  update_id: string;
  agent_id: string;
  agent_name: string;
  content: string;
  created_at: string;
  agent_avatar?: string;
}

interface Borrower {
  id: string;
  name: string;
  mobile_no: string;
  email: string;
  address: string;
}

interface CollectionCase {
  id: string;
  account_balance: number;
  loandisk_id: string;
  photo_url?: string;
  borrower: Borrower;
  status?: string;
  agent_id?: string;
}

interface RawBorrowerData {
  id: string;
  account_balance: number | null;
  loandisk_id: string;
  photo_url?: string;
  status?: string;
  agent_id?: string;
  borrower: Borrower[] | Borrower;
}

interface BorrowerUpdate {
  id: string;
  content: string;
  image_url?: string;
  agent_name: string;
  agent_id: string;
  created_at: string;
  notify: boolean;
  agent_avatar?: string;
  comments?: UpdateComment[];
  likes?: number;
  isLikedByCurrentUser?: boolean;
}

// Add Notification interface
interface Notification {
  recipient_id: string;
  category: string;
  notification_type: string;
  reference_id: string;
  message: string;
  is_read: boolean;
  is_active: boolean;
  expiry_date: string;
  scheduled_date: string;
}

const formatTimeAgo = (date: string) => {
  try {
    const parsedDate = parseISO(date);
    // Format exact date (e.g., Jun 13, 2025)
    const exactDate = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(parsedDate);
    
    // Format relative time (e.g., 3 days ago)
    const relativeTime = formatDistanceToNow(parsedDate, { addSuffix: true });
    
    // Combine both with a dot separator
    return `${exactDate} · ${relativeTime}`;
  } catch (err) {
    return 'some time ago';
  }
};

// New Recent Activity UI styles
const UpdateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  marginBottom: theme.spacing(2),
}));

const UpdateHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const UpdateContent = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const UpdateMedia = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  width: '100%',
  '& img': {
    width: '100%',
    maxHeight: 300,
    objectFit: 'contain',
    backgroundColor: '#f5f5f5',
  },
}));

const UpdateFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

const CommentSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderLeft: '1px solid',
  borderColor: theme.palette.divider,
  paddingLeft: theme.spacing(2),
}));

const CommentItem = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  position: 'relative',
}));

// Main Component
const BorrowerProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [borrowerData, setBorrowerData] = useState<CollectionCase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updateText, setUpdateText] = useState('');
  const [isNotify, setIsNotify] = useState(false);
  const { userProfile } = useAuth();
  const [updates, setUpdates] = useState<BorrowerUpdate[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [userAvatarUrl, setUserAvatarUrl] = useState<string | null>(null);
  const [editingUpdateId, setEditingUpdateId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUpdateId, setSelectedUpdateId] = useState<string | null>(null);
  const [expandedUpdateId, setExpandedUpdateId] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [submittingCommentFor, setSubmittingCommentFor] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [commentMenuAnchorEl, setCommentMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [selectedCommentUpdateId, setSelectedCommentUpdateId] = useState<string | null>(null);
  const [deleteCommentDialogOpen, setDeleteCommentDialogOpen] = useState(false);
  const [showAllCommentsMap, setShowAllCommentsMap] = useState<Record<string, boolean>>({});
  const [likedUpdateIds, setLikedUpdateIds] = useState<string[]>([]);
  const [likesLoading, setLikesLoading] = useState<Record<string, boolean>>({});
  // New reminder tracking state
  const [userReminderMap, setUserReminderMap] = useState<Record<string, boolean>>({});
  const [reminderLoading, setReminderLoading] = useState<Record<string, boolean>>({});
  // New emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
  const [emojiPickerAnchorEl, setEmojiPickerAnchorEl] = useState<null | HTMLElement>(null);
  const [showCommentEmojiPicker, setShowCommentEmojiPicker] = useState<boolean>(false);
  const [commentEmojiPickerAnchorEl, setCommentEmojiPickerAnchorEl] = useState<null | HTMLElement>(null);
  const [activeCommentUpdateId, setActiveCommentUpdateId] = useState<string | null>(null);

  // New states for borrower edit functionality
  const [editBorrowerDialogOpen, setEditBorrowerDialogOpen] = useState(false);
  const [deleteBorrowerDialogOpen, setDeleteBorrowerDialogOpen] = useState(false);
  const [editBorrowerData, setEditBorrowerData] = useState({
    loandisk_id: '',
    agent_id: '',
    status: ''
  });
  const [agents, setAgents] = useState<{id: string, username: string}[]>([]);
  const [isEditingBorrower, setIsEditingBorrower] = useState(false);
  const [balanceLastUpdated, setBalanceLastUpdated] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check if user is admin or superagent
  const isAdminOrSuperagent = userProfile?.role === 'admin' || userProfile?.role === 'superagent';

  // Add theme hook here
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Add these new states near the top with other states
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [reminderDate, setReminderDate] = useState<Date | null>(null);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [currentReminderUpdateId, setCurrentReminderUpdateId] = useState('');
  const [settingReminder, setSettingReminder] = useState(false);
  // Add new state for selected users
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<{id: string, username: string}[]>([]);

  const { notifications, setNotifications } = useNotifications();

  // Add function to fetch available users
  const fetchAvailableUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username')
        .order('username');
        
      if (error) throw error;
      if (data) {
        setAvailableUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    const fetchBorrowerData = async () => {
      if (!id) {
        setError('No borrower ID provided');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const { data, error: supabaseError } = await supabase
          .from('for_collection')
          .select(`
            id,
            account_balance,
            loandisk_id,
            photo_url,
            status,
            agent_id,
            borrower:borrowers!inner (
              id,
              name,
              mobile_no,
              email,
              address
            )
          `)
          .eq('id', id)
          .single();
        console.log('Raw data from Supabase:', data);
        if (supabaseError) {
          console.error('Error fetching borrower:', supabaseError);
          setError('Failed to fetch borrower data');
          return;
        }
        const rawData = data as RawBorrowerData;
        let borrower: Borrower | null = null;
        if (Array.isArray(rawData.borrower)) {
          if (rawData.borrower.length === 0) {
            setError('Borrower not found');
            return;
          }
          borrower = rawData.borrower[0];
        } else if (rawData.borrower) {
          borrower = rawData.borrower;
        } else {
          setError('Borrower not found');
          return;
        }
        
        const loandiskId = cleanLoandiskUniqueNumber(rawData.loandisk_id);
        
        // LoanDisk is the source of truth for collection balances.
        const { data: loanData, error: loanError } = await supabase
          .from('loandisk_loans')
          .select('loan_balance, synced_at')
          .eq('unique_number', loandiskId)
          .order('synced_at', { ascending: true });
          
        if (loanError) {
          console.error('Error fetching loan balances:', loanError);
          setError('Failed to fetch loan balance data');
          return;
        }
        
        // Calculate the total balance by summing all loan_balance values
        const totalBalance = loanData?.reduce((sum, loan) => {
          // Convert to number and handle any potential null/undefined values
          const loanBalance = Number(loan.loan_balance) || 0;
          return sum + loanBalance;
        }, 0) || 0;
        
        // Keep the existing "last updated" display, now using the mirror sync time.
        if (loanData && loanData.length > 0) {
          setBalanceLastUpdated(loanData[0].synced_at);
        }
        
        console.log('Total loan balance calculated:', totalBalance);

        // A manually stored collection photo remains first priority. Otherwise,
        // use the stable photo cached by the LoanDisk sync.
        let resolvedPhotoUrl = rawData.photo_url || '';
        if (!resolvedPhotoUrl && loandiskId) {
          const { data: mirrorBorrower, error: mirrorBorrowerError } = await supabase
            .from('loandisk_borrowers')
            .select('photo_url')
            .eq('unique_number', loandiskId)
            .maybeSingle();
          if (mirrorBorrowerError) {
            console.error('Error fetching LoanDisk borrower photo:', mirrorBorrowerError);
          } else if (mirrorBorrower?.photo_url) {
            resolvedPhotoUrl = mirrorBorrower.photo_url;
          }
        }
        
        const transformedData: CollectionCase = {
          id: rawData.id,
          account_balance: totalBalance, // Use the calculated total balance instead of rawData.account_balance
          loandisk_id: loandiskId,
          photo_url: resolvedPhotoUrl,
          borrower: borrower,
          status: rawData.status || '',
          agent_id: rawData.agent_id || '',
        };
        console.log('Transformed data:', transformedData);
        setBorrowerData(transformedData);

        // Set initial edit data
        setEditBorrowerData({
          loandisk_id: loandiskId,
          agent_id: rawData.agent_id || '',
          status: rawData.status || '',
        });
      } catch (err) {
        console.error('Error in fetchBorrowerData:', err);
        setError('An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };
    fetchBorrowerData();
  }, [id]);

  // Fetch agents list for the dropdown
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, username')
          .in('role', ['agent', 'superagent']);
        
        if (error) throw error;
        setAgents(data || []);
      } catch (error) {
        console.error('Error fetching agents:', error);
      }
    };

    if (isAdminOrSuperagent) {
      fetchAgents();
    }
  }, [isAdminOrSuperagent]);

  const handleEditBorrowerDialogOpen = () => {
    // Reset form data to current values
    if (borrowerData) {
      setEditBorrowerData({
        loandisk_id: borrowerData.loandisk_id || '',
        agent_id: borrowerData.agent_id || '',
        status: borrowerData.status || '',
      });
    }
    setEditBorrowerDialogOpen(true);
  };

  const handleEditBorrowerDialogClose = () => {
    setEditBorrowerDialogOpen(false);
  };

  const handleDeleteBorrowerDialogOpen = () => {
    setDeleteBorrowerDialogOpen(true);
  };

  const handleDeleteBorrowerDialogClose = () => {
    setDeleteBorrowerDialogOpen(false);
  };

  const handleEditBorrowerChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setEditBorrowerData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  const handleEditBorrowerSubmit = async () => {
    if (!id) return;
    
    try {
      setIsEditingBorrower(true);
      const { error } = await supabase
        .from('for_collection')
        .update({
          agent_id: editBorrowerData.agent_id || null,
          status: editBorrowerData.status,
        })
        .eq('id', id);
      
      if (error) throw error;
      
      // Update local state to reflect changes
      if (borrowerData) {
        setBorrowerData({
          ...borrowerData,
          agent_id: editBorrowerData.agent_id,
          status: editBorrowerData.status,
        });
      }
      
      setEditBorrowerDialogOpen(false);
    } catch (err) {
      console.error('Error updating borrower:', err);
      setError(err instanceof Error ? err.message : 'Failed to update borrower details');
    } finally {
      setIsEditingBorrower(false);
    }
  };

  const handleDeleteBorrower = async () => {
    if (!id) return;
    
    try {
      setIsEditingBorrower(true);
      setError(null); // Clear any previous errors
      
      // First, get all update IDs for this borrower
      const { data: borrowerData, error: fetchBorrowerError } = await supabase
        .from('for_collection')
        .select('id')
        .eq('id', id)
        .single();
        
      if (fetchBorrowerError) {
        console.error('Error verifying borrower exists:', fetchBorrowerError);
        throw new Error(`Failed to verify borrower exists: ${fetchBorrowerError.message}`);
      }
      
      if (!borrowerData) {
        setError('Borrower not found in database. It may have already been deleted.');
        return;
      }
      
      console.log('Confirmed borrower exists with ID:', id);
      
      // Get updates for this borrower
      const { data: borrowerUpdates, error: fetchUpdatesError } = await supabase
        .from('borrower_updates')
        .select('id')
        .eq('collection_id', id);
      
      if (fetchUpdatesError) {
        console.error('Error fetching borrower updates:', fetchUpdatesError);
        throw new Error(`Failed to fetch updates: ${fetchUpdatesError.message}`);
      }
      
      const updateIds = (borrowerUpdates || []).map(update => update.id);
      console.log(`Found ${updateIds.length} updates to delete:`, updateIds);
      
      // If there are any updates, delete related comments and likes
      if (updateIds.length > 0) {
        // Check if comments exist first
        const { count: commentCount, error: countCommentsError } = await supabase
          .from('update_comments')
          .select('*', { count: 'exact', head: true })
          .in('update_id', updateIds);
        
        if (countCommentsError) {
          console.error('Error counting comments:', countCommentsError);
          // Don't throw, just log and continue
        } else {
          console.log(`Found ${commentCount} comments to delete`);
        }
        
        // Delete comments for these updates
        const { data: deletedComments, error: commentsError } = await supabase
          .from('update_comments')
          .delete()
          .in('update_id', updateIds)
          .select('id');
        
        if (commentsError) {
          console.error('Error deleting comments:', commentsError);
          // Don't throw here, continue with the process
        }
        
        const commentsCount = deletedComments?.length || 0;
        console.log(`Successfully deleted ${commentsCount} comments`);
        
        // Check if likes exist first
        const { count: likesCountFromDb, error: countLikesError } = await supabase
          .from('update_likes')
          .select('*', { count: 'exact', head: true })
          .in('update_id', updateIds);
        
        if (countLikesError) {
          console.error('Error counting likes:', countLikesError);
          // Don't throw, just log and continue
        } else {
          console.log(`Found ${likesCountFromDb} likes to delete`);
        }
        
        // Delete likes for these updates
        const { data: deletedLikes, error: likesError } = await supabase
          .from('update_likes')
          .delete()
          .in('update_id', updateIds)
          .select('id');
        
        if (likesError) {
          console.error('Error deleting likes:', likesError);
          // Don't throw here, continue with the process
        }
        
        const deletedLikesCount = deletedLikes?.length || 0;
        console.log(`Successfully deleted ${deletedLikesCount} likes`);
      }
      
      // Now check if updates exist first with a count operation
      const { count: updatesCount, error: countUpdatesError } = await supabase
        .from('borrower_updates')
        .select('*', { count: 'exact', head: true })
        .eq('collection_id', id);
      
      if (countUpdatesError) {
        console.error('Error counting updates:', countUpdatesError);
      } else {
        console.log(`Confirmed ${updatesCount} updates to delete for collection_id:`, id);
      }
      
      // Delete the updates
      const { data: deletedUpdates, error: updatesError } = await supabase
        .from('borrower_updates')
        .delete()
        .eq('collection_id', id)
        .select('id');
      
      if (updatesError) {
        console.error('Error deleting updates:', updatesError);
        throw new Error(`Failed to delete updates: ${updatesError.message}`);
      }
      
      const updatesDeleted = deletedUpdates?.length || 0;
      console.log(`Successfully deleted ${updatesDeleted} updates`);
      
      // Check if collection record exists
      const { count: collectionCountFromDb, error: countCollectionError } = await supabase
        .from('for_collection')
        .select('*', { count: 'exact', head: true })
        .eq('id', id);
      
      if (countCollectionError) {
        console.error('Error counting collection records:', countCollectionError);
      } else {
        console.log(`Confirmed ${collectionCountFromDb} collection records to delete with ID:`, id);
      }
      
      // Then delete the main record
      console.log('Attempting to delete from for_collection, id:', id);
      const { data: deletedCollection, error: collectionError } = await supabase
        .from('for_collection')
        .delete()
        .eq('id', id)
        .select('id');
      
      if (collectionError) {
        console.error('Error deleting collection record:', collectionError);
        throw new Error(`Failed to delete collection record: ${collectionError.message}`);
      }
      
      const deletedCollectionCount = deletedCollection?.length || 0;
      console.log(`Successfully deleted ${deletedCollectionCount} collection records`);
      
      if (deletedCollectionCount === 0) {
        // Check if there's a specific RLS error
        setError('No records were deleted. This may be due to permission issues or Row Level Security policies.');
        return;
      }
      
      // Redirect to the collection-list page
      window.location.href = '/collection-list';
    } catch (err) {
      console.error('Error deleting borrower:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete borrower');
    } finally {
      setIsEditingBorrower(false);
      setDeleteBorrowerDialogOpen(false);
    }
  };

  const fetchUpdates = async () => {
    try {
      const { data, error } = await supabase
        .from('borrower_updates')
        .select('*')
        .eq('collection_id', id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const updatesData = data || [];
      const agentIds = Array.from(new Set(updatesData.map((update: any) => update.agent_id)));
      let avatarMap: Record<string, string> = {};
      if (agentIds.length > 0) {
      const { data: avatarsData, error: avatarsError } = await supabase
        .from('user_avatars')
        .select('profile_id, avatar_url')
        .in('profile_id', agentIds);
        if (avatarsError) {
          console.error('Error fetching avatars:', avatarsError);
        } else if (avatarsData) {
          avatarMap = avatarsData.reduce((map: Record<string, string>, avatar: any) => {
            if (!map[avatar.profile_id]) {
              map[avatar.profile_id] = avatar.avatar_url;
            }
            return map;
          }, {});
        }
      }
      
      const updateIds = updatesData.map((update: any) => update.id);
      let commentsMap: Record<string, UpdateComment[]> = {};
      
      if (updateIds.length > 0) {
        const { data: commentsData, error: commentsError } = await supabase
          .from('update_comments')
          .select('*')
          .in('update_id', updateIds)
          .order('created_at', { ascending: true });
        if (commentsError) {
          console.error('Error fetching comments:', commentsError);
        } else if (commentsData) {
          const commentAgentIds = Array.from(new Set(commentsData.map((comment: any) => comment.agent_id)));
          const newAgentIds = commentAgentIds.filter(id => !avatarMap[id]);
          if (newAgentIds.length > 0) {
            const { data: commentAvatarsData } = await supabase
              .from('user_avatars')
              .select('profile_id, avatar_url')
              .in('profile_id', newAgentIds);
            if (commentAvatarsData) {
              commentAvatarsData.forEach((avatar: any) => {
                if (!avatarMap[avatar.profile_id]) {
                  avatarMap[avatar.profile_id] = avatar.avatar_url;
                }
              });
            }
          }
          const commentsWithAvatars = commentsData.map((comment: any) => ({
            ...comment,
            agent_avatar: avatarMap[comment.agent_id] || null,
          }));
          commentsMap = commentsWithAvatars.reduce((map: Record<string, UpdateComment[]>, comment: UpdateComment) => {
            if (!map[comment.update_id]) {
              map[comment.update_id] = [];
            }
            map[comment.update_id].push(comment);
            return map;
          }, {});
        }
      }
      const updatesWithAvatars = updatesData.map((update: any) => ({
        ...update,
        agent_avatar: avatarMap[update.agent_id] || null,
        comments: commentsMap[update.id] || [],
      }));
      setUpdates(updatesWithAvatars);
    } catch (error) {
      console.error('Error fetching updates:', error);
    }
  };

  useEffect(() => {
    fetchUpdates();
  }, [id]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
    }
  };

  const handleAddUpdate = async () => {
    if (!updateText.trim() || !userProfile) return;
    setIsSubmitting(true);
    try {
      let imageUrl = '';
      if (selectedFile) {
        try {
          const fileExt = selectedFile.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
          const filePath = fileName;
          const { error: uploadError } = await supabase.storage
            .from(STORAGE_BUCKET)
            .upload(filePath, selectedFile, { cacheControl: '3600', upsert: false });
          if (uploadError) {
            console.error('Upload error:', uploadError);
            throw uploadError;
          }
          const { data: { publicUrl } } = supabase.storage
            .from(STORAGE_BUCKET)
            .getPublicUrl(filePath);
          imageUrl = publicUrl;
        } catch (uploadErr) {
          console.error('Error uploading file:', uploadErr);
          throw new Error(uploadErr instanceof Error ? uploadErr.message : 'Failed to upload image');
        }
      }
      
      // Insert the update
      const { data: updateData, error: updateError } = await supabase
        .from('borrower_updates')
        .insert({
          collection_id: id,
          agent_id: userProfile.id,
          content: updateText,
          image_url: imageUrl || null,
          notify: isNotify,
          agent_name: userProfile.username || 'Unknown Agent',
        })
        .select('id')
        .single();
      
      if (updateError) throw updateError;
      
      // Process mentions and create notifications
      if (updateData) {
        await processMentionsAndNotify(
          updateText,
          userProfile.id,
          userProfile.username || 'Unknown Agent',
          updateData.id,
          false,
          undefined,
          borrowerData?.borrower.name
        );
      }
      
      await fetchUpdates();
      setUpdateText('');
      setIsNotify(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error adding update:', err);
      setError(err instanceof Error ? err.message : 'Failed to add update');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchUserAvatar = async () => {
      if (!userProfile?.id) return;
      try {
        const { data, error } = await supabase
          .from('user_avatars')
          .select('avatar_url')
          .eq('profile_id', userProfile.id)
          .single();
        if (error) throw error;
        setUserAvatarUrl(data?.avatar_url || null);
      } catch (error) {
        console.error('Error fetching user avatar:', error);
      }
    };
    fetchUserAvatar();
  }, [userProfile?.id]);

  const handleUpdateMenuOpen = (event: React.MouseEvent<HTMLElement>, updateId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedUpdateId(updateId);
  };

  const handleUpdateMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleStartEditing = () => {
    handleUpdateMenuClose();
    const updateToEdit = updates.find(update => update.id === selectedUpdateId);
    if (updateToEdit) {
      setEditingUpdateId(updateToEdit.id);
      setEditText(updateToEdit.content);
    }
  };

  const handleCancelEditing = () => {
    setEditingUpdateId(null);
    setEditText('');
  };

  const handleSaveEdit = async () => {
    if (!editingUpdateId || !editText.trim() || !userProfile) return;
    try {
      setIsSubmitting(true);
      const { error } = await supabase
        .from('borrower_updates')
        .update({ content: editText.trim() })
        .eq('id', editingUpdateId);
      if (error) throw error;
      
      // Update local state
      setUpdates(prevUpdates =>
        prevUpdates.map(update =>
          update.id === editingUpdateId ? { ...update, content: editText.trim() } : update
        )
      );
      
      // Process mentions in edited update
      await processMentionsAndNotify(
        editText.trim(),
        userProfile.id,
        userProfile.username || 'Unknown Agent',
        editingUpdateId,
        false,
        undefined,
        borrowerData?.borrower.name
      );
      
      setEditingUpdateId(null);
      setEditText('');
    } catch (err) {
      console.error('Error saving edit:', err);
      setError(err instanceof Error ? err.message : 'Failed to save edit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUpdate = () => {
    if (!selectedUpdateId) return;
    handleUpdateMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUpdateId) {
      setError("Cannot delete: No update selected");
      setDeleteDialogOpen(false);
      return;
    }
    const updateIdToDelete = selectedUpdateId;
    setIsSubmitting(true);
    try {
      await supabase.from('update_comments').delete().eq('update_id', updateIdToDelete);
      const { error: updateError } = await supabase
        .from('borrower_updates')
        .delete()
        .eq('id', updateIdToDelete)
        .select();
      if (updateError) throw new Error(`Failed to delete update: ${updateError.message}`);
      setUpdates(prevUpdates => prevUpdates.filter(update => update.id !== updateIdToDelete));
    } catch (err) {
      console.error('Error in delete process:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete update. Please try again.');
    } finally {
      setIsSubmitting(false);
      setDeleteDialogOpen(false);
      setSelectedUpdateId(null);
    }
  };

  const handleCloseDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleToggleComments = (updateId: string) => {
    setExpandedUpdateId(prev => (prev === updateId ? null : updateId));
  };

  const handleSubmitComment = async (updateId: string) => {
    if (!commentText.trim() || !userProfile) return;
    try {
      setSubmittingCommentFor(updateId);
      const { error: tableCheckError } = await supabase.from('update_comments').select('id').limit(1);
      if (tableCheckError) throw new Error('Comments table may not exist. Please create it first.');
      const { data, error } = await supabase
        .from('update_comments')
        .insert({
          update_id: updateId,
          agent_id: userProfile.id,
          agent_name: userProfile.username || 'Unknown Agent',
          content: commentText.trim(),
        })
        .select();
      if (error) throw error;
      if (data && data.length > 0) {
        const newComment: UpdateComment = { ...data[0], agent_avatar: userAvatarUrl || undefined };
        setUpdates(prevUpdates =>
          prevUpdates.map(update =>
            update.id === updateId
              ? { ...update, comments: [...(update.comments || []), newComment] }
              : update
          )
        );
        
        // Process mentions in the comment
        await processMentionsAndNotify(
          commentText.trim(),
          userProfile.id,
          userProfile.username || 'Unknown Agent',
          data[0].id,
          true, // This is a comment
          updateId, // Parent update ID
          borrowerData?.borrower.name
        );
        
        // Find the original post author and notify them about the comment
        const originalUpdate = updates.find(update => update.id === updateId);
        if (originalUpdate && originalUpdate.agent_id !== userProfile.id) {
          // Don't notify if the commenter is the same as the post author
          const today = new Date();
          const expiryDate = new Date();
          expiryDate.setDate(today.getDate() + 30); // Expire in 30 days
          
          // Create notification for the post author
          await supabase
            .from('notifications')
            .insert({
              recipient_id: originalUpdate.agent_id,
              category: 'mention',
              notification_type: 'comment_mention',
              reference_id: data[0].id, // Use the comment ID as reference for proper navigation
              message: `${userProfile.username || 'Someone'} commented on your post${borrowerData?.borrower.name ? ` for ${borrowerData.borrower.name}` : ''}.`,
              is_read: false,
              is_active: true,
              expiry_date: expiryDate.toISOString(),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          
          console.log(`Notification sent to post author ${originalUpdate.agent_id} about new comment`);
        }
      }
      setCommentText('');
    } catch (err) {
      console.error('Error adding comment:', err);
      setError(err instanceof Error ? `Failed to add comment: ${err.message}` : 'Failed to add comment. Please check database setup.');
    } finally {
      setSubmittingCommentFor(null);
    }
  };

  const handleCommentMenuOpen = (event: React.MouseEvent<HTMLElement>, commentId: string, updateId: string) => {
    event.stopPropagation();
    setCommentMenuAnchorEl(event.currentTarget);
    setSelectedCommentId(commentId);
    setSelectedCommentUpdateId(updateId);
  };

  const handleCommentMenuClose = () => {
    setCommentMenuAnchorEl(null);
  };

  const handleStartEditingComment = () => {
    handleCommentMenuClose();
    if (!selectedCommentId || !selectedCommentUpdateId) return;
    const updateWithComment = updates.find(update => update.id === selectedCommentUpdateId);
    if (!updateWithComment || !updateWithComment.comments) return;
    const commentToEdit = updateWithComment.comments.find(comment => comment.id === selectedCommentId);
    if (commentToEdit) {
      setEditingCommentId(commentToEdit.id);
      setEditCommentText(commentToEdit.content);
    }
  };

  const handleCancelEditingComment = () => {
    setEditingCommentId(null);
    setEditCommentText('');
  };

  const handleSaveEditComment = async () => {
    if (!editingCommentId || !editCommentText.trim()) return;
    try {
      setIsSubmitting(true);
      const { error } = await supabase
        .from('update_comments')
        .update({ content: editCommentText.trim() })
        .eq('id', editingCommentId);
      if (error) throw error;
      
      // Find the update that contains this comment
      const updateWithComment = updates.find(update => 
        update.comments?.some(comment => comment.id === editingCommentId)
      );
      
      // Update the UI
      setUpdates(prevUpdates =>
        prevUpdates.map(update => {
          if (!update.comments) return update;
          const updatedComments = update.comments.map(comment =>
            comment.id === editingCommentId ? { ...comment, content: editCommentText.trim() } : comment
          );
          return { ...update, comments: updatedComments };
        })
      );
      
      // Process mentions if this is a comment with a valid parent update
      if (userProfile && updateWithComment) {
        await processMentionsAndNotify(
          editCommentText.trim(),
          userProfile.id,
          userProfile.username || 'Unknown Agent',
          editingCommentId,
          true, // This is a comment
          updateWithComment.id, // Parent update ID
          borrowerData?.borrower.name
        );
      }
      
      setEditingCommentId(null);
      setEditCommentText('');
    } catch (err) {
      console.error('Error saving comment edit:', err);
      setError(err instanceof Error ? err.message : 'Failed to save comment edit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = () => {
    if (!selectedCommentId) return;
    handleCommentMenuClose();
    setDeleteCommentDialogOpen(true);
  };

  const handleConfirmDeleteComment = async () => {
    if (!selectedCommentId) {
      setError("Cannot delete: No comment selected");
      setDeleteCommentDialogOpen(false);
      return;
    }
    const commentIdToDelete = selectedCommentId;
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('update_comments')
        .delete()
        .eq('id', commentIdToDelete)
        .select();
      if (error) throw new Error(`Failed to delete comment: ${error.message}`);
      setUpdates(prevUpdates =>
        prevUpdates.map(update => {
          if (!update.comments) return update;
          return { ...update, comments: update.comments.filter(comment => comment.id !== commentIdToDelete) };
        })
      );
    } catch (err) {
      console.error('Error in delete comment process:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete comment. Please try again.');
    } finally {
      setIsSubmitting(false);
      setDeleteCommentDialogOpen(false);
      setSelectedCommentId(null);
      setSelectedCommentUpdateId(null);
    }
  };

  const handleCloseCommentDialog = () => {
    setDeleteCommentDialogOpen(false);
  };

  const handleToggleShowAllComments = (updateId: string) => {
    setShowAllCommentsMap(prev => ({ ...prev, [updateId]: !prev[updateId] }));
  };

  const fetchLikes = async () => {
    if (!userProfile?.id) return;
    try {
      const { error: tableCheckError } = await supabase.from('update_likes').select('id').limit(1);
      if (tableCheckError) {
        console.error('Likes table check error:', tableCheckError);
        return;
      }
      const { data: likedData, error: likedError } = await supabase
        .from('update_likes')
        .select('update_id')
        .eq('agent_id', userProfile.id);
      if (likedError) {
        console.error('Error fetching liked updates:', likedError);
        return;
      }
      const likedIds = likedData?.map(like => like.update_id) || [];
      setLikedUpdateIds(likedIds);
      const updateIds = updates.map(update => update.id);
      let likesMap: Record<string, number> = {};
      if (updateIds.length > 0) {
        for (const updateId of updateIds) {
          const { count, error } = await supabase
            .from('update_likes')
            .select('*', { count: 'exact', head: true })
            .eq('update_id', updateId);
          if (error) {
            console.error(`Error counting likes for update ${updateId}:`, error);
          } else {
            likesMap[updateId] = count as number || 0;
          }
        }
        setUpdates(prev =>
          prev.map(update => ({
            ...update,
            likes: likesMap[update.id] || 0,
            isLikedByCurrentUser: likedIds.includes(update.id),
          }))
        );
      }
    } catch (error) {
      console.error('Error in fetchLikes:', error);
    }
  };

  // Add function to fetch user's reminders after the fetchLikes function
  const fetchUserReminders = async () => {
    if (!userProfile?.id || !id) return;
    
    try {
      console.log('Fetching reminders for user:', userProfile.id);
      const { data, error } = await supabase
        .from('notifications')
        .select('reference_id')
        .eq('recipient_id', userProfile.id)
        .eq('category', 'reminder')
        .eq('is_active', true); // Add this filter for active reminders only
      
      if (error) {
        console.error('Error fetching reminders:', error);
        throw error;
      }
      
      console.log('Fetched reminders:', data?.length || 0);
      
      // Create a map of update IDs for which the user has set reminders
      const reminderMap: Record<string, boolean> = {};
      data?.forEach(notification => {
        if (notification.reference_id) {
          reminderMap[notification.reference_id] = true;
        }
      });
      
      setUserReminderMap(reminderMap);
    } catch (error) {
      console.error('Error fetching user reminders:', error);
    }
  };

  // Modify useEffect to fetch reminders and likes
  useEffect(() => {
    if (updates.length > 0 && userProfile?.id) {
      fetchLikes();
      fetchUserReminders(); // Add user dependency to ensure it runs when user changes
    }
  }, [updates, userProfile?.id, id]);

  // Separate effect to fetch reminders immediately when component mounts or user changes
  useEffect(() => {
    if (userProfile?.id && id) {
      fetchUserReminders();
    }
  }, [userProfile?.id, id]);

  const handleToggleLike = async (updateId: string) => {
    if (!userProfile?.id) return;
    setLikesLoading(prev => ({ ...prev, [updateId]: true }));
    try {
      const isCurrentlyLiked = likedUpdateIds.includes(updateId);
      if (isCurrentlyLiked) {
        const { error } = await supabase
          .from('update_likes')
          .delete()
          .eq('update_id', updateId)
          .eq('agent_id', userProfile.id);
        if (error) throw error;
        setLikedUpdateIds(prev => prev.filter(id => id !== updateId));
        setUpdates(prev =>
          prev.map(update =>
            update.id === updateId
              ? { ...update, likes: (update.likes || 0) - 1, isLikedByCurrentUser: false }
              : update
          )
        );
      } else {
        const { error } = await supabase
          .from('update_likes')
          .insert({ update_id: updateId, agent_id: userProfile.id });
        if (error) throw error;
        setLikedUpdateIds(prev => [...prev, updateId]);
        setUpdates(prev =>
          prev.map(update =>
            update.id === updateId
              ? { ...update, likes: (update.likes || 0) + 1, isLikedByCurrentUser: true }
              : update
          )
        );
      }
    } catch (err) {
      console.error('Error toggling like:', err);
      if (err instanceof Error) {
        const errorMessage = err.message.includes('update_likes')
          ? 'The likes feature requires an "update_likes" table. Please create it first with columns: id, update_id, agent_id, created_at.'
          : err.message;
        setError(errorMessage);
      } else {
        setError('Failed to update like status');
      }
    } finally {
      setLikesLoading(prev => ({ ...prev, [updateId]: false }));
    }
  };

  const handleEmojiPickerOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setEmojiPickerAnchorEl(event.currentTarget);
    setShowEmojiPicker(true);
  };

  const handleEmojiPickerClose = () => {
    setShowEmojiPicker(false);
    setEmojiPickerAnchorEl(null);
  };

  const handleCommentEmojiPickerOpen = (event: React.MouseEvent<HTMLButtonElement>, updateId: string) => {
    setCommentEmojiPickerAnchorEl(event.currentTarget);
    setActiveCommentUpdateId(updateId);
    setShowCommentEmojiPicker(true);
  };

  const handleCommentEmojiPickerClose = () => {
    setShowCommentEmojiPicker(false);
    setCommentEmojiPickerAnchorEl(null);
    setActiveCommentUpdateId(null);
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setUpdateText(prevText => prevText + emojiData.emoji);
    handleEmojiPickerClose();
  };

  const handleCommentEmojiClick = (emojiData: EmojiClickData) => {
    setCommentText(prevText => prevText + emojiData.emoji);
    handleCommentEmojiPickerClose();
  };

  // Add this function to handle going back
  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  // Add these new handlers before the return statement
  const handleOpenSmsDialog = () => {
    setSmsDialogOpen(true);
    setSmsError(null);
  };

  const handleCloseSmsDialog = () => {
    setSmsDialogOpen(false);
    setSmsMessage('');
    setSmsError(null);
  };

  const handleSendSms = async () => {
    if (!smsMessage.trim() || !borrowerData?.borrower.mobile_no || !userProfile || !id) {
      setSmsError('Missing required information');
      return;
    }
    
    setSendingSms(true);
    setSmsError(null);
    
    try {
      // First try to send the SMS
      try {
        await sendSMS({
          number: borrowerData.borrower.mobile_no,
          message: smsMessage.trim()
        });
      } catch (smsError) {
        console.error('SMS sending failed:', smsError);
        setSmsError('SMS service is currently unavailable. Message will be saved in updates.');
        // Continue execution to at least save the update
      }

      // Add update about SMS attempt
      const updateData = {
        collection_id: id,
        agent_id: userProfile.id,
        content: `SMS Message: "${smsMessage.trim()}" - To: ${borrowerData.borrower.mobile_no} - By: ${userProfile.username}`,
        notify: true,
        agent_name: userProfile.username || 'Unknown Agent',
        created_at: new Date().toISOString(),
        deleted_at: null
      };

      const { data: insertedUpdate, error: updateError } = await supabase
        .from('borrower_updates')
        .insert([updateData])
        .select('*')
        .single();

      if (updateError) {
        throw new Error(`Failed to save update: ${updateError.message}`);
      }

      // Refresh updates list
      await fetchUpdates();
      
      // Only close dialog if we successfully saved the update
      handleCloseSmsDialog();
      
      // Show success message
      setError(null);
    } catch (error) {
      console.error('Error in SMS process:', error);
      setSmsError(error instanceof Error ? error.message : 'Failed to process SMS request');
    } finally {
      setSendingSms(false);
    }
  };

  const handleOpenReminderDialog = (updateId: string) => {
    // Check if the current user already has a reminder for this update
    if (userReminderMap[updateId]) {
      alert('You already have a reminder set for this update.');
      return;
    }
    
    setCurrentReminderUpdateId(updateId);
    setReminderDate(null); // Reset date when opening dialog
    setSelectedUsers([]); // Reset selected users
    fetchAvailableUsers(); // Fetch available users
    setReminderDialogOpen(true);
  };

  const handleCloseReminderDialog = () => {
    setReminderDialogOpen(false);
    setReminderDate(null);
    setSelectedUsers([]);
  };

  const handleSetReminder = async () => {
    if (!userProfile?.id || !reminderDate || !currentReminderUpdateId || !borrowerData) return;
    
    setSettingReminder(true);
    setReminderLoading(prev => ({ ...prev, [currentReminderUpdateId]: true }));
    
    try {
      // Find the relevant update
      const update = updates.find(u => u.id === currentReminderUpdateId);
      if (!update) throw new Error('Update not found');
      
      console.log('Setting reminder for update:', update);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
      
      const reminderDay = new Date(reminderDate);
      reminderDay.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
      
      // Set the expiry date to a month after the reminder date
      const expiryDate = new Date(reminderDate);
      expiryDate.setMonth(expiryDate.getMonth() + 1);
      
      // Create a day-before reminder
      const dayBefore = new Date(reminderDate);
      dayBefore.setDate(dayBefore.getDate() - 1);
      dayBefore.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
      
      // Format a shortened version of the update content (first 30 chars)
      const shortenedContent = update.content.length > 30 
        ? update.content.substring(0, 30) + '...' 
        : update.content;
      
      // Get borrower name
      const borrowerName = borrowerData.borrower.name;
      
      // Check if day before is today, tomorrow, or in the future
      const isDayBeforeToday = dayBefore.getTime() === today.getTime();
      const isReminderToday = reminderDay.getTime() === today.getTime();
      
      // Create day-before notification if applicable
      if (dayBefore >= today) {
        const { error: reminderError } = await supabase
          .from('notifications')
          .insert({
            recipient_id: userProfile.id,
            category: 'reminder',
            notification_type: 'reminder',
            reference_id: update.id,
            message: `"${shortenedContent}" is due tomorrow for ${borrowerName} (ID: ${id})`,
            is_read: false,
            is_active: isDayBeforeToday,
            expiry_date: expiryDate.toISOString(),
            scheduled_date: dayBefore.toISOString(),
          });
          
        if (reminderError) {
          console.error('Error creating day-before reminder:', reminderError);
          throw reminderError;
        }
      }
      
      // Create the actual day reminder for current user
      const { error: todayReminderError } = await supabase
        .from('notifications')
        .insert({
          recipient_id: userProfile.id,
          category: 'reminder',
          notification_type: 'reminder',
          reference_id: update.id,
          message: `"${shortenedContent}" is due today for ${borrowerName} (ID: ${id})`,
          is_read: false,
          is_active: isReminderToday,
          expiry_date: expiryDate.toISOString(),
          scheduled_date: reminderDate.toISOString(),
        });
        
      if (todayReminderError) {
        console.error('Error creating today reminder:', todayReminderError);
        throw todayReminderError;
      }
      
      // Create reminders for selected users
      if (selectedUsers.length > 0) {
        const notifications: Notification[] = [];
        
        // Create day-before notifications for selected users
        if (dayBefore >= today) {
          selectedUsers.forEach(userId => {
            notifications.push({
              recipient_id: userId,
              category: 'reminder',
              notification_type: 'reminder',
              reference_id: update.id,
              message: `"${shortenedContent}" is due tomorrow for ${borrowerName} (ID: ${id})`,
              is_read: false,
              is_active: isDayBeforeToday,
              expiry_date: expiryDate.toISOString(),
              scheduled_date: dayBefore.toISOString(),
            });
          });
        }
        
        // Create actual day notifications for selected users
        selectedUsers.forEach(userId => {
          notifications.push({
            recipient_id: userId,
            category: 'reminder',
            notification_type: 'reminder',
            reference_id: update.id,
            message: `"${shortenedContent}" is due today for ${borrowerName} (ID: ${id})`,
            is_read: false,
            is_active: isReminderToday,
            expiry_date: expiryDate.toISOString(),
            scheduled_date: reminderDate.toISOString(),
          });
        });
        
        // Insert all notifications
        if (notifications.length > 0) {
          const { error: notificationError } = await supabase
            .from('notifications')
            .insert(notifications);
            
          if (notificationError) {
            console.error('Error creating notifications for selected users:', notificationError);
            throw notificationError;
          }
        }
      }
      
      // Update the reminder map to show the button as active immediately
      setUserReminderMap(prev => ({
        ...prev,
        [currentReminderUpdateId]: true
      }));
      
      // Refresh reminders to ensure the state is consistent
      await fetchUserReminders();
      
      handleCloseReminderDialog();
      // Show success message or notification
      setError(null);
      alert('Reminder set successfully');
      
    } catch (error) {
      console.error('Error setting reminder:', error);
      setError(error instanceof Error ? error.message : 'Failed to set reminder');
    } finally {
      setSettingReminder(false);
      setReminderLoading(prev => ({ ...prev, [currentReminderUpdateId]: false }));
    }
  };

  // Add this after the fetchUserReminders function
  const activateScheduledReminders = async () => {
    try {
      // Only activate reminders that are not yet marked as inactive
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('notifications')
        .update({ is_active: true })
        .eq('category', 'reminder')
        .eq('is_active', false) // Only consider currently inactive reminders
        .gte('scheduled_date', today) // Only future reminders
        .eq('recipient_id', userProfile?.id || ''); 
        // Removed: .is('deleted_at', null)
      
      if (error) {
        console.error('Error activating scheduled reminders:', error);
        return;
      }
      
      // Refresh reminders for the current user after activation
      if (userProfile?.id) {
        await fetchUserReminders();
      }
    } catch (error) {
      console.error('Error in activateScheduledReminders:', error);
    }
  };

  // Call this function when component mounts
  useEffect(() => {
    activateScheduledReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.id]);

  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        console.log("Loading timeout triggered");
        setLoading(false);
        setError("Loading timed out - please check network connection and try again");
      }, 10000); // 10 second timeout
      
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const handleDismissNotification = async (notificationId: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString(),
          deleted_at: new Date().toISOString() // Add this back to permanently mark as deleted
        })
        .eq('id', notificationId);

      if (error) throw error;
      
      // Check if notifications context is available
      if (setNotifications) {
        // Update notifications state to remove this notification
        setNotifications((prev: any[]) => prev.filter((n: any) => n.id !== notificationId));
      }
      
      // Also update the reminder map for instant UI feedback
      const notificationItem = notifications?.find((n: any) => n.id === notificationId);
      if (notificationItem?.notification_type === 'reminder' && notificationItem.reference_id) {
        setUserReminderMap(prev => ({
          ...prev,
          [notificationItem.reference_id as string]: false
        }));
      }
    } catch (error) {
      console.error('Error dismissing notification:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !borrowerData || !borrowerData.borrower) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Failed to load borrower data'}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: 'background.default', 
      minHeight: '100vh', 
      py: isMobile ? 1 : 3, // Reduce top padding on mobile
      px: 0 
    }}>
      <Container 
        maxWidth={false}
        disableGutters={true} // Always disable gutters to ensure consistency
        sx={{ 
          px: isMobile ? 0 : 3,
          width: '100%',
          mx: 0 // Ensure no margin on either side
        }}
      >
        {/* Back Button - Reduce vertical spacing and ensure consistent horizontal padding */}
        <Box sx={{ 
          mb: isMobile ? 1 : 3, // Reduce bottom margin on mobile
          pl: isMobile ? 1 : 0, // Use left padding only to match design
          pr: isMobile ? 1 : 0
        }}>
          <Button 
            startIcon={<ArrowBack />} 
            onClick={handleBack}
            variant="text"
            sx={{ 
              color: 'text.secondary',
              borderRadius: '8px',
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              py: 0.5,
              '&:hover': { 
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main
              }
            }}
          >
            Back
          </Button>
        </Box>
        
        <Paper 
          elevation={0}
          sx={{
            // On phones: flatten the outer card (no border/shadow/bg) so the inner
            // Account Balance / Contact sections are the only card layer (no card-on-card).
            // Keep small padding so sub-cards don't touch the screen edge.
            p: { xs: 1, sm: 3 },
            mx: 0, // Ensure zero margin on both sides
            ml: 0, // Explicitly set left margin to 0
            mr: 0, // Explicitly set right margin to 0
            border: { xs: 'none', sm: '1px solid' },
            borderColor: 'divider',
            borderRadius: { xs: 0, sm: 3 },
            boxShadow: { xs: 'none', sm: '0 2px 16px rgba(0,0,0,0.04)' },
            bgcolor: { xs: 'transparent', sm: 'background.paper' },
            overflow: 'hidden',
            width: '100%',
            boxSizing: 'border-box' // Ensure padding is included in width calc
          }}
        >
          {/* Profile Header - Update to use more width */}
          <Box
            sx={{
              mb: isMobile ? 2 : 4, // Reduce bottom margin on mobile
              display: 'flex',
              flexDirection: isMobile ? 'column-reverse' : 'row',
              alignItems: isMobile ? 'center' : 'flex-start',
              justifyContent: 'space-between',
              gap: isMobile ? 1 : 3, // Reduce gap on mobile
              textAlign: isMobile ? 'center' : 'initial',
              width: '100%'
            }}
          >
            <Box sx={{ 
              flex: 1, 
              width: isMobile ? '100%' : 'auto',
              maxWidth: '100%' // Ensure it takes full width on mobile
            }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: 1, 
                  fontWeight: 800,
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  lineHeight: 1.2,
                  background: 'linear-gradient(45deg, #333333 30%, #666666 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent'
                }}
              >
                {borrowerData.borrower.name}{' '}
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 'normal',
                    fontSize: '0.9rem',
                    color: 'text.secondary',
                    display: isMobile ? 'block' : 'inline',
                    ...(isMobile 
                      ? { mt: 0.5 } 
                      : { ml: 1 }
                    )
                  }}
                >
                  ({borrowerData.loandisk_id})
                </Typography>
              </Typography>
              
              <Box sx={{ 
                mt: 3, 
                p: 2, 
                borderRadius: 2,
                bgcolor: alpha(theme.palette.success.light, 0.05),
                border: '1px solid',
                borderColor: alpha(theme.palette.success.main, 0.1),
              }}>
                <Typography 
                  variant="subtitle1" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 1, 
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Account Balance
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  // Removed duplicate alignItems declaration
                  flexWrap: 'wrap',
                  ...(isMobile 
                    ? { 
                        flexDirection: 'column',
                        alignItems: 'center' 
                      } 
                    : { 
                        flexDirection: 'row',
                        alignItems: 'flex-start' 
                      }
                  )
                }}>
                  <Typography 
                    variant="h4" 
                    color="success.main" 
                    sx={{ 
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      letterSpacing: '-0.5px',
                      fontSize: isMobile ? '1.5rem' : '2rem',
                    }}
                  >
                    {borrowerData.account_balance.toLocaleString('en-PH', { 
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  
                  <Link 
                    to={`/borrower-accounts/${borrowerData.loandisk_id}/${borrowerData.borrower.id}`}
                    component={RouterLink}
                    color="primary"
                    underline="none"
                    sx={{ 
                      ml: isMobile ? 0 : 2,
                      mt: isMobile ? 2 : 0, 
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 5,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      fontSize: '0.825rem', 
                      fontWeight: 500,
                      display: 'inline-flex',
                      alignItems: 'center',
                      transition: 'all 0.2s',
                      '&:hover': { 
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    See Details
                    <ArrowForwardIos fontSize="inherit" sx={{ ml: 0.5, fontSize: '0.7rem' }} />
                  </Link>
                </Box>
                
                {balanceLastUpdated && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mt: 2, 
                      p: 0.75, 
                      px: 1.5, 
                      borderRadius: 6, 
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontSize: '0.775rem',
                      bgcolor: alpha('#f44336', 0.08),
                      border: '1px solid',
                      borderColor: alpha('#f44336', 0.12),
                      color: alpha('#d32f2f', 0.9)
                    }}
                  >
                    <InfoIcon sx={{ fontSize: '0.9rem', mr: 0.5, opacity: 0.8 }} />
                    Last updated: {formatDistanceToNow(parseISO(balanceLastUpdated || ""), { addSuffix: true })}
                  </Typography>
                )}
              </Box>
            </Box>
            
            {/* Find the Box component containing the ProfileAvatar */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              gap: 1 
            }}>
              {/* Conditionally show avatar only if there's an image or it's not mobile */}
              {(!isMobile || borrowerData?.photo_url) && (
                <ProfileAvatar src={borrowerData?.photo_url || undefined} />
              )}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row',
                gap: 0.5,
                mt: isMobile && !borrowerData?.photo_url ? 0 : 1
              }}>
                {isAdminOrSuperagent && (
                  <IconButton
                    size="small"
                    onClick={handleEditBorrowerDialogOpen}
                    sx={{
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>

          {/* Contact Information - Update padding */}
          <Box sx={{ 
            mb: 4,
            p: isMobile ? 1.5 : 2.5, // Reduce padding on mobile
            borderRadius: 2,
            bgcolor: theme.palette.background.default,
            border: '1px solid',
            borderColor: theme.palette.divider,
            width: '100%' // Ensure full width
          }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 2, 
                fontWeight: 600,
                fontSize: '0.875rem',
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Contact Information
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ 
                  p: 1,
                  borderRadius: 1,
                  display: 'flex',
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  flexShrink: 0
                }}>
                  <LocationIcon color="info" fontSize="small" />
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Address
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 500,
                    wordBreak: 'break-word'
                  }}>
                    {borrowerData.borrower.address || 'No address provided'}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ 
                  p: 1,
                  borderRadius: 1,
                  display: 'flex',
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  flexShrink: 0
                }}>
                  <PhoneIcon color="success" fontSize="small" />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Phone
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {borrowerData.borrower.mobile_no || 'No phone number provided'}
                    </Typography>
                    {borrowerData.borrower.mobile_no && (
                      <IconButton
                        size="small"
                        onClick={handleOpenSmsDialog}
                        sx={{ 
                          ml: 1,
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.success.main, 0.2),
                          }
                        }}
                      >
                        <SendIcon fontSize="small" color="success" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ 
                  p: 1,
                  borderRadius: 1,
                  display: 'flex',
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  flexShrink: 0
                }}>
                  <EmailIcon color="warning" fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Email
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      color: borrowerData.borrower.email ? 'text.primary' : 'text.disabled',
                      wordBreak: 'break-word'
                    }}
                  >
                    {borrowerData.borrower.email || 'Email not provided'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Add Update Section - mobile optimized */}
          <Box sx={{ mb: 4, width: '100%' }}>
            <input type="file" accept="image/*" style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileSelect} />
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, width: '100%' }}>
              <Avatar src={userAvatarUrl || undefined} sx={{ width: 40, height: 40, display: isMobile ? 'none' : 'flex' }}>
                {userProfile?.username?.charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ position: 'relative' }}>
                  <MentionInput
                    value={updateText}
                    onChange={setUpdateText}
                    placeholder={isMobile ? "Add update... @ to mention" : "Add an update... Use @ to mention someone"}
                    multiline
                    rows={isMobile ? 3 : 2}
                    fullWidth
                    variant="outlined"
                    disabled={isSubmitting}
                  />
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    mt: 1, 
                    gap: 1,
                    flexWrap: isMobile ? 'wrap' : 'nowrap'
                  }}>
                    <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={isSubmitting}>
                      <AttachFileIcon color={selectedFile ? 'primary' : 'inherit'} />
                    </IconButton>
                    <IconButton size="small" onClick={handleEmojiPickerOpen} disabled={isSubmitting}>
                      <EmojiIcon color="inherit" />
                    </IconButton>
                    <Button
                      variant={isMobile ? "contained" : "text"}
                      size="small"
                      onClick={handleAddUpdate}
                      disabled={!updateText.trim() || isSubmitting}
                      startIcon={<SendIcon />}
                      sx={{ 
                        borderRadius: '20px',
                        ml: isMobile ? 'auto' : 0
                      }}
                    >
                      {isMobile ? "Post" : ""}
                    </Button>
                  </Box>
                </Box>
                {showEmojiPicker && (
                  <Box
                    sx={{
                      position: 'absolute',
                      zIndex: 10,
                      mt: 1,
                      boxShadow: 3,
                      borderRadius: 1,
                      overflow: 'hidden',
                      right: isMobile ? 0 : 'auto',
                    }}
                  >
                    <ClickAwayListener onClickAway={handleEmojiPickerClose}>
                      <Box>
                        <EmojiPicker 
                          onEmojiClick={handleEmojiClick}
                          theme={Theme.LIGHT}
                          width={isMobile ? 280 : 320}
                          height={isMobile ? 300 : 350}
                        />
                      </Box>
                    </ClickAwayListener>
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'space-between' }}>
                  {selectedFile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {selectedFile?.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedFile(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Recent Activity Section */}
          <Box sx={{ mt: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              mb: 3,
              ...(isMobile 
                ? {
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 2
                  }
                : {
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 0
                  }
              )
            }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Recent Activity
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" size="small" sx={{ borderRadius: 4, px: 2, textTransform: 'none', fontWeight: 'normal' }}>
                  Filter
                </Button>
                <Button variant="outlined" size="small" sx={{ borderRadius: 4, px: 2, textTransform: 'none', fontWeight: 'normal' }}>
                  Sort
                </Button>
              </Box>
            </Box>
            
            {updates.map((update) => (
              <UpdateContainer 
                key={update.id}
                sx={{ 
                  p: isMobile ? 1.5 : 2,
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: isMobile ? 1 : 2,
                }}
              >
                <Avatar 
                  src={update.agent_avatar || undefined} 
                  sx={{ 
                    width: isMobile ? 32 : 40, 
                    height: isMobile ? 32 : 40,
                    alignSelf: isMobile ? 'center' : 'flex-start',
                    display: isMobile ? 'none' : 'flex',
                  }}
                >
                  {update.agent_name.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <UpdateHeader>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      width: '100%',
                      alignItems: 'center',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isMobile && (
                          <Avatar 
                            src={update.agent_avatar || undefined} 
                            sx={{ width: 24, height: 24 }}
                          >
                            {update.agent_name.charAt(0)}
                          </Avatar>
                        )}
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            {update.agent_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimeAgo(update.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                      {userProfile?.id === update.agent_id && (
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleUpdateMenuOpen(e, update.id)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </UpdateHeader>
                  
                  {/* Keep the rest of the update content unchanged */}
                  {update.notify && (
                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}>
                      Important
                    </Typography>
                  )}
                  {editingUpdateId === update.id ? (
                    <Box sx={{ mb: 2 }}>
                      <MentionInput
                        fullWidth
                        multiline
                        rows={3}
                        value={editText}
                        onChange={setEditText}
                        placeholder="Edit your update... Use @ to mention someone"
                        disabled={isSubmitting}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                        <Button size="small" onClick={handleCancelEditing} disabled={isSubmitting} sx={{ textTransform: 'none' }}>
                          Cancel
                        </Button>
                        <Button variant="contained" size="small" onClick={handleSaveEdit} disabled={isSubmitting || !editText.trim()} sx={{ textTransform: 'none' }}>
                          Save
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <UpdateContent variant="body1" sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                        {update.content}
                      </UpdateContent>
                      {update.image_url && (
                        <UpdateMedia>
                          <img src={update.image_url} alt="Update attachment" style={{ maxHeight: isMobile ? '200px' : '300px' }} />
                        </UpdateMedia>
                      )}
                    </>
                  )}
                  
                  {/* Update Footer - icon-only version */}
                  <UpdateFooter sx={{ flexWrap: 'wrap', gap: isMobile ? 1 : 2 }}>
                    <Button
                      startIcon={<CommentIcon fontSize="small" />}
                      size="small"
                      onClick={() => handleToggleComments(update.id)}
                      sx={{ 
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        minWidth: 'unset',
                        px: 1
                      }}
                    >
                      {(update.comments?.length || 0) > 0 ? `${update.comments?.length}` : ''}
                    </Button>
                    
                    <Button
                      startIcon={
                        <ThumbUpIcon
                          fontSize="small"
                          color={update.isLikedByCurrentUser ? 'primary' : 'inherit'}
                          sx={{ 
                            fill: update.isLikedByCurrentUser ? 'currentColor' : 'none',
                            stroke: update.isLikedByCurrentUser ? 'none' : 'currentColor',
                            strokeWidth: update.isLikedByCurrentUser ? 0 : 1,
                          }}
                        />
                      }
                      size="small"
                      onClick={() => handleToggleLike(update.id)}
                      disabled={likesLoading[update.id]}
                      sx={{ 
                        color: update.isLikedByCurrentUser ? 'primary.main' : 'text.secondary',
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        minWidth: 'unset',
                        px: 1
                      }}
                    >
                      {likesLoading[update.id] ? '...' : `${update.likes || 0}`}
                    </Button>
                    
                    <IconButton
                      size="small"
                      onClick={() => handleOpenReminderDialog(update.id)}
                      disabled={reminderLoading[update.id]}
                      sx={{ 
                        color: userReminderMap[update.id] ? theme.palette.success.main : 'text.secondary',
                      }}
                    >
                      <AlarmIcon 
                        fontSize="small" 
                        sx={{ 
                          color: userReminderMap[update.id] ? theme.palette.success.main : 'inherit'
                        }} 
                      />
                    </IconButton>
                  </UpdateFooter>
                  
                  {/* Comment section - kept unchanged */}
                  {update.comments && update.comments.length > 0 && (
                    <CommentSection>
                      {update.comments.slice(-2).map((comment) => (
                        <CommentItem key={comment.id}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Avatar src={comment.agent_avatar} sx={{ width: 28, height: 28 }}>
                              {comment.agent_name.charAt(0)}
                    </Avatar>
                            <Box sx={{ flex: 1, backgroundColor: '#f5f7f9', p: 1.5, borderRadius: 2, position: 'relative' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                                  {comment.agent_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatTimeAgo(comment.created_at)}
                                </Typography>
                              </Box>
                              {editingCommentId === comment.id ? (
                                <Box sx={{ mt: 1 }}>
                                  <MentionInput
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={editCommentText}
                                    onChange={setEditCommentText}
                                    placeholder="Edit your comment... Use @ to mention someone"
                                    disabled={isSubmitting}
                                  />
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                                    <Button size="small" onClick={handleCancelEditingComment} disabled={isSubmitting} sx={{ textTransform: 'none' }}>
                                      Cancel
                                    </Button>
                                    <Button variant="contained" size="small" onClick={handleSaveEditComment} disabled={isSubmitting || !editCommentText.trim()} sx={{ textTransform: 'none' }}>
                                      Save
                                    </Button>
                                  </Box>
                                </Box>
                              ) : (
                                <Typography variant="body2" sx={{ fontSize: '0.9rem', mt: 1 }}>
                                  {comment.content}
                                </Typography>
                              )}
                              {userProfile?.id === comment.agent_id && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleCommentMenuOpen(e, comment.id, update.id)}
                                  sx={{ position: 'absolute', right: 4, top: 4, width: 24, height: 24 }}
                                >
                                  <MoreVertIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        </CommentItem>
                      ))}
                      {update.comments.length > 2 && (
                        <Box sx={{ mb: 2 }}>
                          <Link
                            component="button"
                      variant="caption" 
                            onClick={() => handleToggleShowAllComments(update.id)}
                            sx={{ cursor: 'pointer' }}
                          >
                            {showAllCommentsMap[update.id]
                              ? 'Show less comments'
                              : `Show ${update.comments.length - 2} more comments`}
                          </Link>
                        </Box>
                      )}
                      {showAllCommentsMap[update.id] && update.comments.length > 2 && (
                        <Box>
                          {update.comments.slice(0, -2).map((comment) => (
                            <CommentItem key={comment.id}>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Avatar src={comment.agent_avatar} sx={{ width: 28, height: 28 }}>
                                  {comment.agent_name.charAt(0)}
                                </Avatar>
                                <Box sx={{ flex: 1, backgroundColor: '#f5f7f9', p: 1.5, borderRadius: 2, position: 'relative' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                                      {comment.agent_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {formatTimeAgo(comment.created_at)}
                    </Typography>
                  </Box>
                                  {editingCommentId === comment.id ? (
                                    <Box sx={{ mt: 1 }}>
                                      <MentionInput
                                        fullWidth
                                        multiline
                                        rows={2}
                                        value={editCommentText}
                                        onChange={setEditCommentText}
                                        placeholder="Edit your comment... Use @ to mention someone"
                                        disabled={isSubmitting}
                                      />
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                                        <Button size="small" onClick={handleCancelEditingComment} disabled={isSubmitting} sx={{ textTransform: 'none' }}>
                                          Cancel
                                        </Button>
                                        <Button variant="contained" size="small" onClick={handleSaveEditComment} disabled={isSubmitting || !editCommentText.trim()} sx={{ textTransform: 'none' }}>
                                          Save
                                        </Button>
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" sx={{ fontSize: '0.9rem', mt: 1 }}>
                                      {comment.content}
                  </Typography>
                                  )}
                                  {userProfile?.id === comment.agent_id && (
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleCommentMenuOpen(e, comment.id, update.id)}
                                      sx={{ position: 'absolute', right: 4, top: 4, width: 24, height: 24 }}
                                    >
                                      <MoreVertIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  )}
                                </Box>
                              </Box>
                            </CommentItem>
                          ))}
                        </Box>
                      )}
                    </CommentSection>
                  )}
                  
                  <Collapse in={expandedUpdateId === update.id} timeout="auto" unmountOnExit>
                    <CommentSection>
                      <CommentItem>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <Avatar src={userAvatarUrl || undefined} sx={{ width: 28, height: 28 }}>
                            {userProfile?.username?.charAt(0)}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <MentionInput
                              value={commentText}
                              onChange={setCommentText}
                              placeholder="Add a comment... Use @ to mention someone"
                              multiline
                              rows={1}
                              fullWidth
                              variant="outlined"
                              disabled={submittingCommentFor === update.id}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleCommentEmojiPickerOpen(e, update.id)}
                              >
                                <EmojiIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={!commentText.trim() || submittingCommentFor === update.id}
                                onClick={() => handleSubmitComment(update.id)}
                                color="primary"
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      </CommentItem>
                    </CommentSection>
                  </Collapse>
                </Box>
              </UpdateContainer>
            ))}
            
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleUpdateMenuClose}>
              <MenuItem onClick={handleStartEditing}>
                <EditIcon fontSize="small" sx={{ mr: 1 }} />
                Edit
              </MenuItem>
              <MenuItem onClick={handleDeleteUpdate}>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Delete
              </MenuItem>
            </Menu>
            
            <Menu anchorEl={commentMenuAnchorEl} open={Boolean(commentMenuAnchorEl)} onClose={handleCommentMenuClose}>
              <MenuItem onClick={handleStartEditingComment}>
                <EditIcon fontSize="small" sx={{ mr: 1 }} />
                Edit
              </MenuItem>
              <MenuItem onClick={handleDeleteComment}>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Delete
              </MenuItem>
            </Menu>
          </Box>
        </Paper>
        
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDialog}
          aria-labelledby="delete-dialog-title"
          PaperProps={{ elevation: 1, sx: { borderRadius: 2, maxWidth: '400px' } }}
        >
          <DialogTitle id="delete-dialog-title" sx={{ pb: 1 }}>
            Confirm Deletion
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this update? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDialog} color="inherit" sx={{ mr: 1 }}>
              CANCEL
            </Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'DELETING...' : 'DELETE'}
            </Button>
          </DialogActions>
        </Dialog>
        
        <Dialog
          open={deleteCommentDialogOpen}
          onClose={handleCloseCommentDialog}
          aria-labelledby="delete-comment-dialog-title"
          aria-describedby="delete-comment-dialog-description"
        >
          <DialogTitle id="delete-comment-dialog-title">Delete Comment</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-comment-dialog-description">
              Are you sure you want to delete this comment?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCommentDialog} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDeleteComment} color="error" variant="contained" autoFocus disabled={isSubmitting}>
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Borrower Dialog */}
        <Dialog 
          open={editBorrowerDialogOpen} 
          onClose={handleEditBorrowerDialogClose}
          PaperProps={{ elevation: 1, sx: { borderRadius: 2, maxWidth: '500px' } }}
        >
          <DialogTitle sx={{ pb: 1 }}>Edit Borrower Details</DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                fullWidth
                id="loandisk_id"
                label="Loandisk ID"
                name="loandisk_id"
                value={editBorrowerData.loandisk_id}
                InputProps={{ readOnly: true }}
                helperText="Managed by Loandisk"
                sx={{ mb: 2 }}
              />
              
              <TextField
                select
                margin="normal"
                fullWidth
                id="agent_id"
                label="Assign Agent"
                name="agent_id"
                value={editBorrowerData.agent_id}
                onChange={handleEditBorrowerChange}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.username}
                  </MenuItem>
                ))}
              </TextField>
              
              <TextField
                select
                margin="normal"
                fullWidth
                id="status"
                label="Status"
                name="status"
                value={editBorrowerData.status}
                onChange={handleEditBorrowerChange}
                sx={{ mb: 2 }}
              >
                <MenuItem value="For Collection">For Collection</MenuItem>
                <MenuItem value="For CI">For CI</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleEditBorrowerDialogClose} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleEditBorrowerSubmit} 
              variant="contained" 
              color="primary"
              disabled={isEditingBorrower}
            >
              {isEditingBorrower ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Borrower Dialog */}
        <Dialog
          open={deleteBorrowerDialogOpen}
          onClose={handleDeleteBorrowerDialogClose}
          PaperProps={{ elevation: 1, sx: { borderRadius: 2, maxWidth: '400px' } }}
        >
          <DialogTitle sx={{ pb: 1 }}>Delete Borrower</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this borrower? This will permanently remove all borrower data and associated updates. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleDeleteBorrowerDialogClose} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteBorrower} 
              variant="contained" 
              color="error"
              disabled={isEditingBorrower}
            >
              {isEditingBorrower ? 'Deleting...' : 'Delete Borrower'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* SMS Dialog */}
        <Dialog
          open={smsDialogOpen}
          onClose={handleCloseSmsDialog}
          PaperProps={{ 
            elevation: 1,
            sx: { 
              borderRadius: 2,
              maxWidth: '500px',
              width: '100%'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            Send SMS to {borrowerData?.borrower.name}
          </DialogTitle>
          <DialogContent>
            {smsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {smsError}
              </Alert>
            )}
            <TextField
              fullWidth
              multiline
              rows={4}
              value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
              placeholder="Type your message here..."
              disabled={sendingSms}
              sx={{ mt: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Recipient: {borrowerData?.borrower.mobile_no}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={handleCloseSmsDialog} 
              disabled={sendingSms}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSendSms}
              disabled={!smsMessage.trim() || sendingSms}
              startIcon={<SendIcon />}
            >
              {sendingSms ? 'Sending...' : 'Send SMS'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reminder Dialog */}
        <Dialog
          open={reminderDialogOpen}
          onClose={handleCloseReminderDialog}
          PaperProps={{ 
            elevation: 1,
            sx: { 
              borderRadius: 2,
              maxWidth: '500px',
              width: '100%'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            Set Reminder for {borrowerData?.borrower.name}
          </DialogTitle>
          <DialogContent>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Reminder Date"
                value={reminderDate}
                onChange={(newValue) => setReminderDate(newValue)}
                slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
              />
            </LocalizationProvider>
            
            {/* Add user selection */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Notify Additional Users (Optional)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availableUsers
                  .filter(user => user.id !== userProfile?.id) // Exclude current user
                  .map(user => (
                    <Chip
                      key={user.id}
                      label={user.username}
                      onClick={() => {
                        if (selectedUsers.includes(user.id)) {
                          setSelectedUsers(prev => prev.filter(id => id !== user.id));
                        } else {
                          setSelectedUsers(prev => [...prev, user.id]);
                        }
                      }}
                      color={selectedUsers.includes(user.id) ? "primary" : "default"}
                      sx={{ m: 0.5 }}
                    />
                  ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={handleCloseReminderDialog} 
              disabled={settingReminder}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSetReminder}
              disabled={!reminderDate || settingReminder}
              startIcon={<AlarmIcon />}
            >
              {settingReminder ? 'Setting...' : 'Set Reminder'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default BorrowerProfile;
